import React from 'react';

import api from 'api';
import useRequest from 'hooks/useRequest';
import {
  AnnouncementInboxResponse,
  AnnouncementItem,
  parseAnnouncementInbox,
} from 'types/announcement';

type AnnouncementsContextValue = {
  messages: AnnouncementItem[];
  latestMessage: AnnouncementItem | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
};

const AnnouncementsContext = React.createContext<AnnouncementsContextValue>({
  messages: [],
  latestMessage: null,
  isLoading: false,
  refresh: async () => {},
});

export const AnnouncementsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = React.useState<AnnouncementItem[]>([]);
  const { doRequest, isLoading } = useRequest<AnnouncementInboxResponse>(api.getAnnouncementInbox);

  const refresh = React.useCallback(async () => {
    const data = await doRequest(null);
    setMessages(parseAnnouncementInbox(data));
  }, [doRequest]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const value = React.useMemo(
    () => ({
      messages,
      latestMessage: messages[0] ?? null,
      isLoading,
      refresh,
    }),
    [messages, isLoading, refresh],
  );

  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
    </AnnouncementsContext.Provider>
  );
};

export const useAnnouncements = () => React.useContext(AnnouncementsContext);

export default AnnouncementsContext;
